import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { i18n } from '@/locales';

import {
  getSubscription,
  patchSubscriptionBillingInformations,
  patchSubscriptionCompanyContractor,
  patchSubscriptionAnnualConsumption,
  patchSubscriptionDirectDebit,
  patchSubscriptionEnergyOfferInformations,
  patchSubscriptionEquipmentControlOptions,
  patchSubscriptionFlexConsent,
  patchSubscriptionIndividualContractor,
  patchSubscriptionInstallation,
  patchSubscriptionInstallationAddress,
  patchSubscriptionPdl,
  patchSubscriptionProfile,
  postSubscriptionPdl,
  patchSubscriptionPromotionCode,
  patchSubscriptionConsents,
  patchSubscriptionSignature,
  patchSubscriptionCustomerDeclarations,
} from '@/api/subscription.api';
import { useEquipmentsDeclarationStore, useStepStore, useToastSnackbarStore } from '@/stores/index';

import type {
  AnnualConsumption,
  BudgetPrices,
  DirectDebit,
  PatchSubscriptionBillingInformationsRequest,
  Subscription,
  SubscriptionAddress,
  SubscriptionCompanyContractor,
  SubscriptionEnergyOfferInformations,
  SubscriptionIndividualContractor,
  SubscriptionInstallation,
  SubscriptionProfile,
  PatchSubscriptionConsentsRequest,
  PatchSubscriptionCustomerDeclarationsRequest,
} from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';

import { ESubscriptionRouteName } from '@/constants/router.constants';
import { EStorageKey } from '@/constants/storateKeys.constants';
import {
  EBatteryOptionAvailable,
  EEquipmentControlOption,
  EEquipmentInstallationStatus,
  EInstallationPerformer,
  EMainHeatingSource,
  EOperator,
  ESubscriptionType,
  EWaterHeatingSource,
} from '@/constants/subscription.constants';
import { useSessionStorage } from '@vueuse/core';

export default defineStore('subscription', () => {
  const t = i18n.global.t;

  const toastSnackBar = useToastSnackbarStore();
  const stepStore = useStepStore();
  const equipmentsDeclarationStore = useEquipmentsDeclarationStore();

  const subscriptionIdFromStorage = useSessionStorage(EStorageKey.SUBSCRIPTION_ID, '');

  const pdlNumber = useSessionStorage(EStorageKey.PDL_NUMBER, '');
  const isResubscription = useSessionStorage(EStorageKey.IS_RESUBSCRIPTION, false);

  const currentSubscription = ref<Subscription>({} as Subscription);

  const isGetSubscriptionInProgress = ref(false);
  const isGetSubscriptionSuccessfully = ref(false);
  const isGetSubscriptionInError = ref(false);

  const isSaveSubscriptionInProgress = ref(false);

  const isApplyPromotionCodeInProgress = ref(false);
  const isRemovePromotionCodeInProgress = ref(false);

  const isSignatureUrlLoading = ref(false);

  const getCurrentSubscriptionId = computed(() => {
    return currentSubscription.value?.subscriptionId;
  });

  const promotionCode = computed(() => currentSubscription.value?.promotionCode?.value ?? null);

  const getCurrentSubscriptionConsents = computed(
    () => currentSubscription.value?.consents ?? null
  );

  const getCurrentSubscriptionPdl = computed(() => {
    return currentSubscription.value?.pdl?.value || '';
  });

  const getCurrentSubscriptionActiveContractNumber = computed(
    () => currentSubscription.value?.pdl?.linkedActiveContractNumber || ''
  );

  const getCurrentSubscriptionInstallationAddress = computed(
    () => currentSubscription.value?.installationAddress || null
  );

  const getCurrentSubscriptionProfile = computed(() => currentSubscription.value?.profile || null);
  const getCurrentSubscriptionOptions = computed(() => currentSubscription.value?.options || null);

  const getCurrentSubscriptionInstallation = computed(
    () => currentSubscription.value?.installation || null
  );

  const getCustomerDeclarations = computed(
    () => currentSubscription.value?.customerDeclarations ?? null
  );

  const isEligibleForPreloadedBatteryOffer = computed(
    () => currentSubscription.value?.offers?.preloadedBatteryOffer?.isEligible === true
  );

  const isEligibleForWelcomeOffer = computed(
    () => currentSubscription.value?.offers?.welcomeOffer?.isEligible === true
  );

  const getIsResubscription = computed(() => {
    if (isResubscription.value) return true;
    return currentSubscription.value?.subscriptionType === ESubscriptionType.ReSubscription;
  });

  const isSelfInstalled = computed(() => {
    return (
      getCurrentSubscriptionInstallation.value?.installationPerformedBy ===
      EInstallationPerformer.SELF_INSTALLED
    );
  });

  const isInstalledByInstaller = computed(() => {
    return (
      getCurrentSubscriptionInstallation.value?.installationPerformedBy ===
      EInstallationPerformer.PROFESSIONAL_INSTALLER
    );
  });

  const getHasSolarPanelsFromSubscription = computed(
    () =>
      getCurrentSubscriptionProfile.value?.hasSolarPanels ===
        EEquipmentInstallationStatus.PLANNED_INSTALLATION ||
      getCurrentSubscriptionProfile.value?.hasSolarPanels ===
        EEquipmentInstallationStatus.ALREADY_EQUIPPED
  );

  const getHasPhysicalBattery = computed(
    () =>
      getCurrentSubscriptionProfile.value?.hasPhysicalBattery ===
      EEquipmentInstallationStatus.ALREADY_EQUIPPED
  );

  const getHasRobinDeviceFromSubscription = computed(
    () =>
      getCurrentSubscriptionProfile.value?.hasRobinDevice ===
        EEquipmentInstallationStatus.PLANNED_INSTALLATION ||
      getCurrentSubscriptionProfile.value?.hasRobinDevice ===
        EEquipmentInstallationStatus.ALREADY_EQUIPPED
  );

  const hasAirToWaterHeatPump = computed(
    () =>
      getCurrentSubscriptionProfile.value?.mainHeatingSource ===
      EMainHeatingSource.AirToWaterHeatPump
  );

  const hasElectricWaterHeater = computed(
    () =>
      getCurrentSubscriptionProfile.value?.waterHeatingSource ===
      EWaterHeatingSource.ElectricWaterHeater
  );

  const hasElectricVehicleCharger = computed(
    () =>
      getCurrentSubscriptionProfile.value?.hasMyLight150ElectricVehicleCharger ===
        EEquipmentInstallationStatus.PLANNED_INSTALLATION ||
      getCurrentSubscriptionProfile.value?.hasMyLight150ElectricVehicleCharger ===
        EEquipmentInstallationStatus.ALREADY_EQUIPPED
  );

  const getHasSolarPanels = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasSolarPanelsInstalled;
    }

    return getHasSolarPanelsFromSubscription.value;
  });

  const getHasRobinDevice = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasRobinDeviceInstalled;
    }

    return getHasRobinDeviceFromSubscription.value;
  });

  const getHasSolarPanelsWithRobinDevice = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasSolarPanelsWithRobinDevice;
    }

    return getHasSolarPanelsFromSubscription.value && getHasRobinDeviceFromSubscription.value;
  });

  const getHasSolarPanelsWithoutRobinDevice = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasSolarPanelsWithoutRobinDevice;
    }

    return (
      getHasSolarPanelsFromSubscription.value &&
      getCurrentSubscriptionProfile.value?.hasRobinDevice ===
        EEquipmentInstallationStatus.NOT_EQUIPPED
    );
  });

  const getHasRobinDeviceWithoutSolarPanels = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasRobinDeviceWithoutSolarPanels;
    }

    return (
      getCurrentSubscriptionProfile.value?.hasSolarPanels ===
        EEquipmentInstallationStatus.NOT_EQUIPPED && getHasRobinDeviceFromSubscription.value
    );
  });

  const getHasNoSolarPanelsAndNoRobinDevice = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasNoSolarPanelsAndNoRobinDevice;
    }

    return (
      getCurrentSubscriptionProfile.value?.hasSolarPanels ===
        EEquipmentInstallationStatus.NOT_EQUIPPED &&
      getCurrentSubscriptionProfile.value?.hasRobinDevice ===
        EEquipmentInstallationStatus.NOT_EQUIPPED
    );
  });

  const getHasCompatibleEquipments = computed(() => {
    if (
      !getCurrentSubscriptionProfile.value ||
      stepStore.getCurrentStep?.id === ESubscriptionRouteName.EQUIPMENTS
    ) {
      return equipmentsDeclarationStore.hasCompatibleEquipments;
    }

    return (
      hasAirToWaterHeatPump.value || hasElectricWaterHeater.value || hasElectricVehicleCharger.value
    );
  });

  const isMsbSelected = computed(
    () => getCurrentSubscriptionOptions.value?.battery.option?.type === EBatteryOptionAvailable.MSB
  );

  const isMbSelected = computed(
    () => getCurrentSubscriptionOptions.value?.battery.option?.type === EBatteryOptionAvailable.MB
  );

  const isBatterySelected = computed(() => isMsbSelected.value || isMbSelected.value);

  const isMscSelected = computed(
    () =>
      getCurrentSubscriptionOptions.value?.equipmentControl?.options?.some(
        o => o.type === EEquipmentControlOption.MSC
      ) ?? false
  );

  const isMspSelected = computed(
    () =>
      getCurrentSubscriptionOptions.value?.equipmentControl?.options?.some(
        o => o.type === EEquipmentControlOption.MSP
      ) ?? false
  );

  const isPilotageSelected = computed(() => isMscSelected.value || isMspSelected.value);

  const getBudgetBreakdown = computed(
    () => currentSubscription.value.estimatedAnnualBudgetBreakdown
  );

  const getBudgetPrices = computed<BudgetPrices | null>(() => {
    const budgetBreakdown = getBudgetBreakdown.value;
    if (!budgetBreakdown) return null;

    const { totalAnnualPrice, energy, subscription, options } = budgetBreakdown;
    return {
      totalAnnual: totalAnnualPrice,
      energyAnnual: energy.totalAnnualPrice,
      subscriptionMonthly: subscription.totalMonthlyPrice,
      subscriptionAnnual: subscription.totalAnnualPrice,
      subscriptionSelfContribution: subscription.selfProducerContribution?.totalAnnualPrice ?? 0,
      msbMonthly: options.msbOption?.totalMonthlyPrice ?? 0,
      msbAnnual: options.msbOption?.totalAnnualPrice ?? 0,
      mbMonthly: options.myBatteryOption?.totalMonthlyPrice ?? 0,
      mbAnnual: options.myBatteryOption?.totalAnnualPrice ?? 0,
      pilotageMonthly: 0,
      pilotageAnnual: 0,
    };
  });

  const getCurrentDirectDebit = computed(() => currentSubscription.value?.directDebit ?? null);

  const hasChoiceNoBattery = computed(() => {
    return getCurrentSubscriptionOptions.value?.battery.requested === false;
  });

  const isEnergySupplyOnly = computed(() => {
    return (
      getHasNoSolarPanelsAndNoRobinDevice.value ||
      (getHasSolarPanelsWithoutRobinDevice.value && hasChoiceNoBattery.value) ||
      (getHasRobinDeviceWithoutSolarPanels.value && !getHasCompatibleEquipments.value) ||
      (getHasSolarPanelsWithRobinDevice.value &&
        !getHasCompatibleEquipments.value &&
        hasChoiceNoBattery.value)
    );
  });

  const setPdl = (pdl: string) => {
    pdlNumber.value = pdl;
  };

  const setIsResubscription = (value: boolean) => {
    isResubscription.value = value;
  };

  const resetFetchSubscriptionState = () => {
    isGetSubscriptionInProgress.value = false;
    isGetSubscriptionSuccessfully.value = false;
    isGetSubscriptionInError.value = false;

    currentSubscription.value = {} as Subscription;
  };

  const fetchSubscription = async () => {
    resetFetchSubscriptionState();

    isGetSubscriptionInProgress.value = true;

    await getSubscription(subscriptionIdFromStorage.value)
      .then(response => {
        setSubscriptionData(response);

        isGetSubscriptionSuccessfully.value = true;
      })
      .catch(error => {
        const toastConfig = getErrorToasterConfig({
          error,
          toasterId: 'subscriptionLayout',
          defaultMessage: t('subscription.common.api.errors.getSubscription'),
        });

        toastSnackBar.addToast(toastConfig);

        isGetSubscriptionInError.value = false;
      })
      .finally(() => {
        isGetSubscriptionInProgress.value = false;
      });
  };

  const setSubscriptionData = (subscription: Subscription) => {
    currentSubscription.value = subscription;
    subscriptionIdFromStorage.value = subscription.subscriptionId;
  };

  const createSubscriptionWithPdl = async (pdl: string) => {
    isSaveSubscriptionInProgress.value = true;
    await postSubscriptionPdl({
      value: pdl,
      operator: EOperator.ENEDIS,
      linkedActiveContractNumber: null,
    })
      .then(response => {
        setSubscriptionData(response);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => (isSaveSubscriptionInProgress.value = false));
  };

  const updateSubscriptionWithPdl = async (pdl: string) => {
    isSaveSubscriptionInProgress.value = true;

    await patchSubscriptionPdl(getCurrentSubscriptionId.value, {
      value: pdl,
      operator: EOperator.ENEDIS,
      linkedActiveContractNumber: null,
    })
      .then(response => {
        setSubscriptionData(response);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => (isSaveSubscriptionInProgress.value = false));
  };

  const createSubscriptionWithContractNumber = async (contractNumber: string) => {
    isSaveSubscriptionInProgress.value = true;

    await postSubscriptionPdl({
      value: pdlNumber.value as string,
      operator: EOperator.ENEDIS,
      linkedActiveContractNumber: contractNumber,
    })
      .then(response => {
        setSubscriptionData(response);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => (isSaveSubscriptionInProgress.value = false));
  };

  const updateSubscriptionWithContractNumber = async (contractNumber: string) => {
    isSaveSubscriptionInProgress.value = true;

    await patchSubscriptionPdl(getCurrentSubscriptionId.value, {
      value: pdlNumber.value as string,
      operator: EOperator.ENEDIS,
      linkedActiveContractNumber: contractNumber,
    })
      .then(response => {
        setSubscriptionData(response);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => (isSaveSubscriptionInProgress.value = false));
  };

  const updateSubscriptionWithInstallationAddress = async (address: SubscriptionAddress) => {
    isSaveSubscriptionInProgress.value = true;

    await patchSubscriptionInstallationAddress(getCurrentSubscriptionId.value, address)
      .then(response => {
        setSubscriptionData(response);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => (isSaveSubscriptionInProgress.value = false));
  };

  const updateSubscriptionWithCustomerDeclarations = async (
    payload: PatchSubscriptionCustomerDeclarationsRequest
  ) => {
    isSaveSubscriptionInProgress.value = true;

    try {
      const response = await patchSubscriptionCustomerDeclarations(
        getCurrentSubscriptionId.value,
        payload
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubscriptionWithProfile = async (payload: SubscriptionProfile) => {
    isSaveSubscriptionInProgress.value = true;

    await patchSubscriptionProfile(getCurrentSubscriptionId.value, payload)
      .then(response => {
        setSubscriptionData(response);
      })
      .catch(error => {
        throw error;
      })
      .finally(() => (isSaveSubscriptionInProgress.value = false));
  };

  const updateSubscriptionWithInstallation = async (payload: SubscriptionInstallation) => {
    isSaveSubscriptionInProgress.value = true;

    await patchSubscriptionInstallation(getCurrentSubscriptionId.value, payload)
      .then(response => {
        setSubscriptionData(response);
      })
      .finally(() => {
        isSaveSubscriptionInProgress.value = false;
      });
  };

  const updateSubcriptionWithEnergyOfferInformations = async (
    energyOfferInformations: SubscriptionEnergyOfferInformations
  ) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionEnergyOfferInformations(
        getCurrentSubscriptionId.value,
        energyOfferInformations
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubcriptionConsumptionInformations = async (annualConsumption: AnnualConsumption) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionAnnualConsumption(
        getCurrentSubscriptionId.value,
        annualConsumption
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubscriptionWithDirectDebit = async (payload: DirectDebit) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionDirectDebit(getCurrentSubscriptionId.value, payload);
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubscriptionWithIndividualContractor = async (
    payload: SubscriptionIndividualContractor
  ) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionIndividualContractor(
        getCurrentSubscriptionId.value,
        payload
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubscriptionWithCompanyContractor = async (
    payload: SubscriptionCompanyContractor
  ) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionCompanyContractor(
        getCurrentSubscriptionId.value,
        payload
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubscriptionWithBillingInformations = async (
    payload: PatchSubscriptionBillingInformationsRequest
  ) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionBillingInformations(
        getCurrentSubscriptionId.value,
        payload
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const updateSubscriptionWithEquipmentControl = async (hasAccepted: boolean) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      await patchSubscriptionFlexConsent(getCurrentSubscriptionId.value, hasAccepted);
      const response = await patchSubscriptionEquipmentControlOptions(
        getCurrentSubscriptionId.value,
        hasAccepted
      );
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const applyPromotionCode = async (value: string) => {
    isApplyPromotionCodeInProgress.value = true;
    try {
      const response = await patchSubscriptionPromotionCode(getCurrentSubscriptionId.value, {
        value,
      });
      setSubscriptionData(response);
    } finally {
      isApplyPromotionCodeInProgress.value = false;
    }
  };

  const removePromotionCode = async () => {
    isRemovePromotionCodeInProgress.value = true;
    try {
      const response = await patchSubscriptionPromotionCode(getCurrentSubscriptionId.value);
      setSubscriptionData(response);
    } finally {
      isRemovePromotionCodeInProgress.value = false;
    }
  };

  const updateSubscriptionConsents = async (payload: PatchSubscriptionConsentsRequest) => {
    isSaveSubscriptionInProgress.value = true;
    try {
      const response = await patchSubscriptionConsents(getCurrentSubscriptionId.value, payload);
      setSubscriptionData(response);
    } finally {
      isSaveSubscriptionInProgress.value = false;
    }
  };

  const fetchContractSignatureUrl = async (): Promise<string> => {
    isSignatureUrlLoading.value = true;
    try {
      const { signatureUrl } = await patchSubscriptionSignature(getCurrentSubscriptionId.value);
      return signatureUrl;
    } finally {
      isSignatureUrlLoading.value = false;
    }
  };

  return {
    subscriptionIdFromStorage,

    currentSubscription,
    isResubscription,
    pdlNumber,

    isGetSubscriptionInProgress,
    isGetSubscriptionSuccessfully,
    isGetSubscriptionInError,

    isSaveSubscriptionInProgress,

    promotionCode,
    isApplyPromotionCodeInProgress,
    isRemovePromotionCodeInProgress,

    isSignatureUrlLoading,

    getCurrentSubscriptionId,

    getCurrentSubscriptionConsents,
    getCurrentSubscriptionPdl,
    getCurrentSubscriptionActiveContractNumber,
    getCurrentSubscriptionInstallationAddress,
    getCurrentSubscriptionProfile,
    getCurrentSubscriptionOptions,
    getCurrentSubscriptionInstallation,
    getCustomerDeclarations,

    isEligibleForPreloadedBatteryOffer,
    isEligibleForWelcomeOffer,
    getHasPhysicalBattery,
    isEnergySupplyOnly,
    hasChoiceNoBattery,
    isMsbSelected,
    isMbSelected,
    isBatterySelected,
    isMscSelected,
    isMspSelected,
    isPilotageSelected,
    isSelfInstalled,
    isInstalledByInstaller,
    hasAirToWaterHeatPump,
    hasElectricWaterHeater,
    hasElectricVehicleCharger,
    getBudgetBreakdown,
    getBudgetPrices,
    getCurrentDirectDebit,
    getIsResubscription,
    getHasSolarPanels,
    getHasRobinDevice,
    getHasNoSolarPanelsAndNoRobinDevice,
    getHasSolarPanelsWithRobinDevice,
    getHasSolarPanelsWithoutRobinDevice,
    getHasRobinDeviceWithoutSolarPanels,
    getHasCompatibleEquipments,

    setPdl,
    setIsResubscription,
    setSubscriptionData,

    fetchSubscription,

    createSubscriptionWithPdl,
    updateSubscriptionWithPdl,

    createSubscriptionWithContractNumber,
    updateSubscriptionWithContractNumber,
    updateSubscriptionWithInstallationAddress,
    updateSubscriptionWithCustomerDeclarations,
    updateSubscriptionWithProfile,
    updateSubscriptionWithInstallation,
    updateSubcriptionWithEnergyOfferInformations,
    updateSubcriptionConsumptionInformations,
    updateSubscriptionWithDirectDebit,
    updateSubscriptionWithIndividualContractor,
    updateSubscriptionWithCompanyContractor,
    updateSubscriptionWithBillingInformations,
    updateSubscriptionWithEquipmentControl,
    applyPromotionCode,
    removePromotionCode,
    updateSubscriptionConsents,
    fetchContractSignatureUrl,
  };
});
